// CFG.PUSH.A1: browser-side Web Push lifecycle.
//
// Encapsulates the five steps required to enable push on this browser:
//   1. Feature-detect serviceWorker + PushManager.
//   2. Register /sw.js (idempotent — returns the existing registration if
//      the SW is already installed).
//   3. Ask the user for Notification permission.
//   4. Subscribe to the browser's push service using the server's VAPID
//      public key (fetched from /api/push/vapid-public-key).
//   5. POST the resulting subscription to /api/push/subscribe.
//
// The helper is intentionally a single function rather than a hook so it
// can be called from a button click handler without re-rendering concerns.

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3001';

// ── Feature detection ──────────────────────────────────────────────────────

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// ── Key conversion ─────────────────────────────────────────────────────────

/**
 * PushManager.subscribe() expects the VAPID public key as a Uint8Array, but
 * the server returns the base64url-encoded form. This is the standard
 * web-push conversion routine — base64url → Uint8Array.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

// ── Subscription payload ───────────────────────────────────────────────────

/**
 * PushSubscription.toJSON() returns { endpoint, keys: { p256dh, auth } } but
 * the types erase the key shape. Narrow it once here so callers get a typed
 * structure to hand to fetch().
 */
interface SerializedSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

function serializeSubscription(sub: PushSubscription): SerializedSubscription | null {
  const json = sub.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) return null;
  return { endpoint, keys: { p256dh, auth } };
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Register the service worker if needed. Safe to call more than once — the
 * browser deduplicates by script URL. Returns null on unsupported browsers.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[push] service worker registration failed:', err);
    return null;
  }
}

/**
 * Full enable flow: request permission, subscribe, POST to API. Returns true
 * on success, false if permission was denied or anything upstream failed. The
 * caller (a button click handler) decides how to surface the outcome.
 */
export async function enablePushNotifications(token: string): Promise<boolean> {
  if (!isPushSupported()) return false;

  const registration = await registerServiceWorker();
  if (!registration) return false;

  // skipWaiting()/clients.claim() are asynchronous, so register() can resolve
  // before the SW has reached the 'activated' state. If we subscribe and
  // persist the endpoint before activation, the first push to arrive can land
  // on a non-active worker and be dropped. serviceWorker.ready resolves only
  // when the registration has an active worker, so we await it here before
  // touching PushManager. This is the canonical fix for the "first push after
  // enable is silently dropped" class of bug.
  //
  // Timeout: serviceWorker.ready has no built-in bound, and a stuck SW
  // activation (corrupted cache, install step hang) would otherwise leave the
  // caller awaiting forever — the banner sits in "Enabling…" indefinitely. We
  // race it against a 5s timeout and fail closed so the UI recovers.
  const SW_READY_TIMEOUT_MS = 5000;
  try {
    const ready = navigator.serviceWorker.ready;
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('serviceWorker.ready timed out')), SW_READY_TIMEOUT_MS);
    });
    await Promise.race([ready, timeout]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[push] service worker never became active:', err);
    return false;
  }

  // The browser requires the permission prompt to run inside a user gesture.
  // This function should only ever be invoked from an onClick handler.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  // Fetch VAPID key. 503 means the server isn't configured — surface as false
  // so the UI can keep the banner visible rather than silently "succeeding".
  let publicKey: string;
  try {
    const res = await fetch(`${API_URL}/api/push/vapid-public-key`);
    if (!res.ok) return false;
    const body = await res.json();
    if (typeof body.publicKey !== 'string') return false;
    publicKey = body.publicKey;
  } catch {
    return false;
  }

  let subscription: PushSubscription | null = null;
  try {
    // Reuse any existing subscription so we don't churn endpoints on every
    // dashboard load. If keys rotate, unsubscribe first and re-subscribe.
    subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // TS lib.dom narrowed applicationServerKey to BufferSource with a
          // strict ArrayBuffer (not SharedArrayBuffer) generic. Our key comes
          // from atob() so it is always backed by a plain ArrayBuffer — cast
          // through BufferSource rather than widening the helper's return
          // type everywhere.
          applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      } catch (subscribeErr) {
        // Two-tab race: another tab may have called subscribe() between our
        // getSubscription() and our subscribe() call. The browser surfaces
        // that as an InvalidStateError on the second caller. Re-read the
        // current subscription — if one now exists, the race converged
        // successfully and we can continue. Otherwise surface the failure.
        subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          // eslint-disable-next-line no-console
          console.warn('[push] subscribe failed:', subscribeErr);
          return false;
        }
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[push] subscribe failed:', err);
    return false;
  }

  const serialized = serializeSubscription(subscription);
  if (!serialized) return false;

  try {
    const res = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(serialized),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Best-effort unsubscribe. Removes the browser-side subscription AND tells
 * the API to forget the row. Safe to call when no subscription exists.
 */
export async function disablePushNotifications(token: string): Promise<void> {
  if (!isPushSupported()) return;

  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  try {
    await subscription.unsubscribe();
  } catch {
    // Best-effort — continue with server-side cleanup regardless.
  }

  try {
    await fetch(`${API_URL}/api/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ endpoint }),
    });
  } catch {
    /* ignore — row will be pruned on next failed dispatch */
  }
}
